import { useState, useMemo } from 'react';
import { mockEPIRequests, mockIncidents, mockDenunciations } from '@/mocks/submissions';
import { mockUsers } from '@/mocks/users';
import { sectors } from '@/mocks/formData';
import type { EPIRequest, Incident, Denunciation } from '@/types';

type TabType = 'epi' | 'incidentes' | 'denuncias';

const tabs: { key: TabType; label: string; icon: string }[] = [
  { key: 'epi', label: 'Solicitações de EPI', icon: 'ri-tools-line' },
  { key: 'incidentes', label: 'Incidentes', icon: 'ri-alarm-warning-line' },
  { key: 'denuncias', label: 'Denúncias', icon: 'ri-shield-user-line' },
];

const defaultReportFields: Record<TabType, string[]> = {
  epi: ['ID', 'Colaborador', 'Setor', 'Motivo', 'Urgência', 'Observacao', 'Itens', 'Status', 'Justificativa', 'Data'],
  incidentes: ['ID', 'Colaborador', 'Setor', 'No setor', 'Tipo', 'Descrição', 'Foto', 'Declaracao', 'Status', 'Justificativa', 'Data'],
  denuncias: ['ID', 'Identificação', 'Setor', 'Assedio explicado', 'Tipo', 'Autores', 'Anexo', 'Detalhes', 'Nível', 'Ultima ocorrencia', 'Acesso retorno', 'Observacoes', 'Status', 'Justificativa', 'Data'],
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const statusColors: Record<string, string> = {
  Pendente: 'bg-amber-50 text-amber-700',
  Aprovado: 'bg-emerald-50 text-emerald-700',
  Negado: 'bg-red-50 text-red-700',
  'Em análise': 'bg-blue-50 text-blue-700',
  'Concluído': 'bg-emerald-50 text-emerald-700',
};

type ReportRow = Record<string, string | number>;

function getReportFileName(tab: TabType, extension: 'csv') {
  const suffix = new Date().toISOString().slice(0, 10);
  return `relatorio-${tab}-${suffix}.${extension}`;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function escapeCsvCell(value: string | number) {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
}

function exportRowsToCsv(rows: ReportRow[], fileName: string) {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(escapeCsvCell).join(';'),
    ...rows.map((row) => headers.map((header) => escapeCsvCell(row[header])).join(';')),
  ].join('\r\n');

  downloadBlob(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }), fileName);
}

export default function AdminRelatorios() {
  const [activeTab, setActiveTab] = useState<TabType>('epi');
  const [search, setSearch] = useState('');
  const [filterSetor, setFilterSetor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedFields, setSelectedFields] = useState<Record<TabType, string[]>>(defaultReportFields);

  const filteredEPIs = useMemo(() => {
    return mockEPIRequests.filter((item) => {
      const visibleId = item.displayId || item.id;
      const matchSearch = !search || item.userName.toLowerCase().includes(search.toLowerCase()) || visibleId.toLowerCase().includes(search.toLowerCase());
      const matchSetor = filterSetor === 'all' || item.setor === filterSetor;
      const matchStatus = filterStatus === 'all' || item.status === filterStatus;
      const matchUser = filterUser === 'all' || item.userId === filterUser;
      const matchDateFrom = !dateFrom || item.createdAt >= dateFrom;
      const matchDateTo = !dateTo || item.createdAt <= dateTo + 'T23:59:59Z';
      return matchSearch && matchSetor && matchStatus && matchUser && matchDateFrom && matchDateTo;
    });
  }, [search, filterSetor, filterStatus, filterUser, dateFrom, dateTo]);

  const filteredIncidents = useMemo(() => {
    return mockIncidents.filter((item) => {
      const visibleId = item.displayId || item.id;
      const matchSearch = !search || item.userName.toLowerCase().includes(search.toLowerCase()) || item.tipoIncidente.toLowerCase().includes(search.toLowerCase()) || visibleId.toLowerCase().includes(search.toLowerCase());
      const matchSetor = filterSetor === 'all' || item.setor === filterSetor;
      const matchUser = filterUser === 'all' || item.userId === filterUser;
      const matchDateFrom = !dateFrom || item.createdAt >= dateFrom;
      const matchDateTo = !dateTo || item.createdAt <= dateTo + 'T23:59:59Z';
      return matchSearch && matchSetor && matchUser && matchDateFrom && matchDateTo;
    });
  }, [search, filterSetor, filterUser, dateFrom, dateTo]);

  const filteredDenunciations = useMemo(() => {
    return mockDenunciations.filter((item) => {
      const matchSearch = !search || (item.nomeIdentificado || 'Anônima').toLowerCase().includes(search.toLowerCase()) || item.tipoAssedio.toLowerCase().includes(search.toLowerCase());
      const matchSetor = filterSetor === 'all' || item.setor === filterSetor;
      const matchUser = filterUser === 'all' || item.userId === filterUser;
      const matchDateFrom = !dateFrom || item.createdAt >= dateFrom;
      const matchDateTo = !dateTo || item.createdAt <= dateTo + 'T23:59:59Z';
      return matchSearch && matchSetor && matchUser && matchDateFrom && matchDateTo;
    });
  }, [search, filterSetor, filterUser, dateFrom, dateTo]);

  const totalCount = activeTab === 'epi' ?filteredEPIs.length : activeTab === 'incidentes' ?filteredIncidents.length : filteredDenunciations.length;

  const allReportRows = useMemo<ReportRow[]>(() => {
    if (activeTab === 'epi') {
      return filteredEPIs.map((item) => ({
        ID: item.displayId || item.id,
        Colaborador: item.userName,
        Setor: item.setor,
        Motivo: item.motivo,
        Itens: item.itens.map((i) => `${i.tipo} (${i.q01 + i.q02 + i.qCaixa})`).join(', '),
        Urgência: item.urgencia,
        Observacao: item.observacao || '',
        Status: item.status,
        Justificativa: item.statusReason || '',
        Data: formatDate(item.createdAt),
      }));
    }

    if (activeTab === 'incidentes') {
      return filteredIncidents.map((item) => ({
        ID: item.displayId || item.id,
        Colaborador: item.userName,
        Setor: item.setor,
        'No setor': item.noSetor,
        Tipo: item.tipoIncidente,
        Descrição: item.descricao,
        Foto: item.fotoUrl || '',
        Declaracao: item.declaracaoVerdade ?'Sim' : 'Nao',
        Status: item.status,
        Justificativa: item.statusReason || '',
        Data: formatDate(item.createdAt),
      }));
    }

    return filteredDenunciations.map((item) => ({
      ID: item.displayId || item.id,
      Identificação: item.identificar ?(item.nomeIdentificado || 'Identificada') : 'Anônima',
      Setor: item.setor,
      'Assedio explicado': item.assedioExplicado ?'Sim' : 'Nao',
      Tipo: item.tipoAssedio,
      Autores: item.autoresNome,
      Anexo: item.anexoUrl || '',
      Detalhes: item.detalhes,
      Nível: `${item.nivelAssedio}/9`,
      'Ultima ocorrencia': item.ultimaOcorrencia,
      'Acesso retorno': item.identificar ?item.acessoRetorno : '',
      Observacoes: item.observacoes || '',
      Status: item.status,
      Justificativa: item.statusReason || '',
      Data: formatDate(item.createdAt),
    }));
  }, [activeTab, filteredEPIs, filteredIncidents, filteredDenunciations]);

  const reportRows = useMemo(() => {
    const fields = selectedFields[activeTab];
    return allReportRows.map((row) => fields.reduce<ReportRow>((acc, field) => {
      acc[field] = row[field] ?? '';
      return acc;
    }, {}));
  }, [activeTab, allReportRows, selectedFields]);

  const handleExportExcel = () => {
    exportRowsToCsv(reportRows, getReportFileName(activeTab, 'csv'));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="text-xl font-bold text-portal-gray-900">Relatórios</h1>
          <p className="text-sm text-portal-gray-500">
            Visualize e exporte dados de solicitações, incidentes e denuncias.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={totalCount === 0 || selectedFields[activeTab].length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-file-excel-2-line text-green-600 text-sm"></i>
            Excel
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-portal-gray-100 rounded-xl p-1 mb-6 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ?'bg-white text-portal-gray-800'
                : 'text-portal-gray-500 hover:text-portal-gray-700'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-filter-3-line text-portal-gray-400 text-sm"></i>
            <span className="text-sm font-medium text-portal-gray-700">Filtros</span>
            <span className="text-xs text-portal-gray-400 ml-1">({totalCount} resultados)</span>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="text-xs text-portal-purple hover:text-portal-purple-dark font-medium cursor-pointer whitespace-nowrap"
          >
            {showFilters ?'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showFilters && (
          <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3">
            <div className="relative xl:col-span-3">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-portal-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full text-sm pl-9 pr-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
              />
            </div>
            <select
              value={filterSetor}
              onChange={(e) => setFilterSetor(e.target.value)}
              className="xl:col-span-2 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer min-w-0"
            >
              <option value="all">Todos os setores</option>
              {sectors.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {activeTab === 'epi' && (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="xl:col-span-2 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer min-w-0"
              >
                <option value="all">Todos os status</option>
                <option value="Pendente">Pendentes</option>
                <option value="Aprovado">Aprovados</option>
                <option value="Negado">Negados</option>
              </select>
            )}
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="xl:col-span-2 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer min-w-0"
            >
              <option value="all">Todos os usuários</option>
              {mockUsers.filter((u) => u.perfil === 'colaborador').map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <div className="sm:col-span-2 xl:col-span-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 min-w-0">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full min-w-0 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
                title="Data inicial"
              />
              <span className="text-portal-gray-400 text-xs">at?</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full min-w-0 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
                title="Data final"
              />
            </div>
          </div>
          <div className="border-t border-portal-gray-100 pt-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-xs font-medium text-portal-gray-600">Campos para exportar</span>
              <button
                type="button"
                onClick={() => setSelectedFields((prev) => ({ ...prev, [activeTab]: defaultReportFields[activeTab] }))}
                className="text-xs font-medium text-portal-purple hover:text-portal-purple-dark"
              >
                Selecionar todos
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {defaultReportFields[activeTab].map((field) => (
                <label key={field} className="flex items-center gap-2 text-xs text-portal-gray-600 bg-portal-gray-50 rounded-lg px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFields[activeTab].includes(field)}
                    onChange={(e) => setSelectedFields((prev) => ({
                      ...prev,
                      [activeTab]: e.target.checked
                        ?[...prev[activeTab], field]
                        : prev[activeTab].filter((item) => item !== field),
                    }))}
                    className="h-4 w-4 accent-portal-purple"
                  />
                  {field}
                </label>
              ))}
            </div>
          </div>
          </div>
        )}
      </div>

      {/* EPI Table */}
      {activeTab === 'epi' && (
        <EPITable data={filteredEPIs} />
      )}

      {/* Incidents Table */}
      {activeTab === 'incidentes' && (
        <IncidentTable data={filteredIncidents} />
      )}

      {/* Denunciations Table */}
      {activeTab === 'denuncias' && (
        <DenunciationTable data={filteredDenunciations} />
      )}
    </div>
  );
}

function EPITable({ data }: { data: EPIRequest[] }) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-portal-gray-200 p-12 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-portal-gray-100 flex items-center justify-center">
          <i className="ri-tools-line text-portal-gray-400 text-xl"></i>
        </div>
        <p className="text-sm text-portal-gray-500">Nenhuma solicitação encontrada com esses filtros.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-portal-gray-100 bg-portal-gray-50/50">
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">ID</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Colaborador</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Setor</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Itens</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Urgência</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Status</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Data</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b border-portal-gray-50 hover:bg-portal-gray-50/30 transition-colors">
                <td className="px-5 py-3 text-xs font-mono text-portal-gray-500">{item.displayId || item.id}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-800">{item.userName}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600">{item.setor}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600">{item.itens.map((i) => `${i.tipo} (${i.q01 + i.q02 + i.qCaixa})`).join(', ')}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600 max-w-[180px] truncate">{item.urgencia}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[item.status]}`}>
                    {item.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-portal-gray-500 whitespace-nowrap">{formatDateShort(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IncidentTable({ data }: { data: Incident[] }) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-portal-gray-200 p-12 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-portal-gray-100 flex items-center justify-center">
          <i className="ri-alarm-warning-line text-portal-gray-400 text-xl"></i>
        </div>
        <p className="text-sm text-portal-gray-500">Nenhum incidente encontrado com esses filtros.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-portal-gray-100 bg-portal-gray-50/50">
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">ID</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Colaborador</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Setor</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Tipo</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Descrição</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Data</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b border-portal-gray-50 hover:bg-portal-gray-50/30 transition-colors">
                <td className="px-5 py-3 text-xs font-mono text-portal-gray-500">{item.displayId || item.id}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-800">{item.userName}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600">{item.setor}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600 max-w-[200px] truncate">{item.tipoIncidente}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600 max-w-[250px] truncate">{item.descricao}</td>
                <td className="px-5 py-3 text-xs text-portal-gray-500 whitespace-nowrap">{formatDateShort(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DenunciationTable({ data }: { data: Denunciation[] }) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-portal-gray-200 p-12 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-portal-gray-100 flex items-center justify-center">
          <i className="ri-shield-user-line text-portal-gray-400 text-xl"></i>
        </div>
        <p className="text-sm text-portal-gray-500">Nenhuma denúncia encontrada com esses filtros.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-portal-gray-100 bg-portal-gray-50/50">
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">ID</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Identificação</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Setor</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Tipo</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Autores</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Nível</th>
              <th className="text-left text-xs font-medium text-portal-gray-500 px-5 py-3 whitespace-nowrap">Data</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b border-portal-gray-50 hover:bg-portal-gray-50/30 transition-colors">
                <td className="px-5 py-3 text-xs font-mono text-portal-gray-500">{item.displayId || item.id}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.identificar ? 'bg-purple-50 text-purple-700' : 'bg-portal-gray-100 text-portal-gray-500'}`}>
                    {item.identificar ? (item.nomeIdentificado || 'Identificado') : 'Anônima'}
                  </span>
                </td>
                <td className="px-5 py-3 text-sm text-portal-gray-600">{item.setor}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600 max-w-[200px] truncate">{item.tipoAssedio}</td>
                <td className="px-5 py-3 text-sm text-portal-gray-600 max-w-[150px] truncate">{item.autoresNome}</td>
                <td className="px-5 py-3 text-sm font-medium text-portal-gray-700">{item.nivelAssedio}/9</td>
                <td className="px-5 py-3 text-xs text-portal-gray-500 whitespace-nowrap">{formatDateShort(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
